import React from "react";
import './index.css';

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import {
  doc,
  getDoc,
} from "firebase/firestore";
import {
  onAuthStateChanged,
} from "firebase/auth";
import { auth, db } from "./firebase";
import DashboardLayout from "./layouts/DashboardLayout";
import Login from "./pages/login";
import Providers from "./pages/providers";
import Payouts from "./pages/payouts";
import Services from "./pages/services";
import DashboardHome from "./pages/dashboardhome";
import Users from "./pages/users";
import BookingsDashboard from "./pages/bookings";
import GroceryCategories from "./pages/grocery_categories";
import GroceryProducts from "./pages/grocery_products";
import RealWorkImages from "./pages/work_images";
import HomeOffers from "./pages/homeoffers";
import UserOffers from "./pages/user_offers";
import ServiceOffersSpinner from "./pages/service_offers_spinner";
import DailyBookingUpdates from "./pages/daily_booking_updates";

const AdminRoute = ({ user, isAdmin, children }) => {
  if (!user || !isAdmin) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  const [user, setUser] = React.useState(null);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          const adminRef = doc(db, "admins", currentUser.uid);
          const adminSnap = await getDoc(adminRef);

          if (adminSnap.exists() && adminSnap.data().role === "admin") {
            setUser(currentUser);
            setIsAdmin(true);
          } else {
            setUser(null);
            setIsAdmin(false);
          }
        } catch (error) {
          console.warn("Admin lookup failed; treating user as non-admin", error);
          setUser(currentUser);
          setIsAdmin(false);
        }
      } else {
        setUser(null);
        setIsAdmin(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) return <p className="text-center p-4">Loading...</p>;

  return (
    <Router>
      <Routes>
        <Route path="/offers/:serviceSlug" element={<ServiceOffersSpinner />} />
        <Route path="/login" element={user && isAdmin ? <Navigate to="/" /> : <Login />} />
        <Route
          path="/"
          element={
            <AdminRoute user={user} isAdmin={isAdmin}>
              <DashboardLayout>
                <DashboardHome />
              </DashboardLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/daily-booking-updates"
          element={
            <AdminRoute user={user} isAdmin={isAdmin}>
              <DashboardLayout>
                <DailyBookingUpdates />
              </DashboardLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/services"
          element={
            <AdminRoute user={user} isAdmin={isAdmin}>
              <DashboardLayout>
                <Services />
              </DashboardLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/providers"
          element={
            <AdminRoute user={user} isAdmin={isAdmin}>
              <DashboardLayout>
                <Providers />
              </DashboardLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/payouts"
          element={
            <AdminRoute user={user} isAdmin={isAdmin}>
              <DashboardLayout>
                <Payouts />
              </DashboardLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/users"
          element={
            <AdminRoute user={user} isAdmin={isAdmin}>
              <DashboardLayout>
                <Users />
              </DashboardLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/bookings"
          element={
            <AdminRoute user={user} isAdmin={isAdmin}>
              <DashboardLayout>
                <BookingsDashboard />
              </DashboardLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/grocery-categories"
          element={
            <AdminRoute user={user} isAdmin={isAdmin}>
              <DashboardLayout>
                <GroceryCategories />
              </DashboardLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/grocery-products"
          element={
            <AdminRoute user={user} isAdmin={isAdmin}>
              <DashboardLayout>
                <GroceryProducts />
              </DashboardLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/real-work-images"
          element={
            <AdminRoute user={user} isAdmin={isAdmin}>
              <DashboardLayout>
                <RealWorkImages />
              </DashboardLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/home-offers"
          element={
            <AdminRoute user={user} isAdmin={isAdmin}>
              <DashboardLayout>
                <HomeOffers />
              </DashboardLayout>
            </AdminRoute>
          }
        />
        <Route
          path="/user-offers"
          element={
            <AdminRoute user={user} isAdmin={isAdmin}>
              <DashboardLayout>
                <UserOffers />
              </DashboardLayout>
            </AdminRoute>
          }
        />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
